import { MusicNoteIdentity, MusicScale } from "../../wasm/pkg/wasm";
import { listAllNoteIds } from "../models/wasm.model";

/**
 * Keep track of whether the 12 notes are in the current scale shape/family.
 */
export type NoteScaleMeta = {
  id: MusicNoteIdentity;
  isInScaleFamily: boolean;
  isInScaleShape: boolean;
};

/**
 * O(1) retrieval cost storage, for whether a given note appears in a scale/family/shape.
 */
export type ScaleShapeNoteMap = {
  [note_id: string]: NoteScaleMeta;
};

/**
 * Keep the information for each scale variation we've encountered
 */
type ScaleShapeNoteMapMap = {
  [music_scale_key: string]: ScaleShapeNoteMap;
};

const NOTE_MAPS: ScaleShapeNoteMapMap = {};

/**
 * Pure function, extracted to hide impl.
 * Related to NoteScaleMeta, but not stateful.
 */
export function rotateSortedNotesToBeginWithTonic(sortedNotes: Array<NoteScaleMeta>, newTonic: MusicNoteIdentity): Array<NoteScaleMeta> {
  const tonic_index = sortedNotes.findIndex((sn) => sn.id === newTonic);
  return [...sortedNotes.slice(tonic_index, sortedNotes.length), ...sortedNotes.slice(0, tonic_index)];
}

/**
 * For each note, attach information about whether it's in the current's scale shape and/or family.
 */
export function updateNoteMetaWithLatestScale(oldNoteInfo: Array<NoteScaleMeta>, scale: MusicScale): Array<NoteScaleMeta> {
  const noteMeta = getScaleShapeNoteMap(scale);
  return oldNoteInfo.map((sn) => noteMeta[sn.id]);
}

/**
 * Retrieve or  scale inclusion info for each of the 12 notes.
 *
 * This cache of prior calculated results should never be mutated.
 */
function getScaleShapeNoteMap(scale: MusicScale): ScaleShapeNoteMap {
  const scaleKey = buildMusicScaleKey(scale);
  if (NOTE_MAPS[scaleKey]) {
    return NOTE_MAPS[scaleKey];
  } else {
    NOTE_MAPS[scaleKey] = buildNoteFamilyMap(scale);
    return NOTE_MAPS[scaleKey];
  }
}

export function emptyNoteScaleMeta(id: MusicNoteIdentity): NoteScaleMeta {
  return { id, isInScaleFamily: false, isInScaleShape: false } as NoteScaleMeta;
}

function buildMusicScaleKey(scale: MusicScale) {
  return scale.shape_notes[0].note_id + scale.shape;
}
/**
 * Create a lookup for "is this note in the scale shape/family"
 */
function buildNoteFamilyMap(scale: MusicScale): { [key: string]: NoteScaleMeta } {
  let map: { [key: string]: NoteScaleMeta } = {};
  scale.shape_notes.forEach((sn) => {
    map[sn.note_id] = { id: sn.note_id, isInScaleFamily: true, isInScaleShape: true };
  });
  scale.family.notes.forEach((fn) => {
    if (!map[fn.note_id]) {
      map[fn.note_id] = { id: fn.note_id, isInScaleFamily: true, isInScaleShape: false };
    }
  });
  listAllNoteIds().forEach((id) => {
    if (!map[id]) {
      map[id] = { id, isInScaleFamily: false, isInScaleShape: false };
    }
  });
  return map;
}
